<template>
  <div id="external_domains_section" class="flex flex-col gap-6">
    <div class="flex justify-between items-center">
      <h1 class="text-xl font-semibold text_color">
        {{ COPY.PAGE_TITLE }}
      </h1>
      <a-button
        id="external_domains_connect_button"
        class="primary_medium"
        @click="openAddModal"
      >
        {{ COPY.CONNECT_BUTTON }}
      </a-button>
    </div>

    <div
      v-if="initialLoading"
      class="flex flex-col items-center justify-center gap-2 mt-20"
    >
      <CustomLoader :size="28" />
    </div>

    <EmptyScreen
      v-if="!initialLoading && mappedDomains.length === 0"
      source="external_domains_empty"
      :text="COPY.EMPTY_TITLE"
      :description="COPY.EMPTY_SUBTITLE"
    />

    <div v-if="!initialLoading && mappedDomains.length > 0">
      <CustomTable
        :headers="EXTERNAL_DOMAINS_TABLE_HEADERS"
        :data="mappedDomains"
        :custom-column-render="customColumnRender"
        @refresh="refreshList"
      />
    </div>

    <AddDomainModal
      v-if="addModalVisible"
      v-model:visible="addModalVisible"
      :initial-record="viewTarget"
      @created="refreshList"
    />

    <ConfirmationModal
      v-if="deleteTarget"
      v-model:visible="deleteModalVisible"
      type="delete"
      :title="deleteModalTitle"
      :message="deleteModalMessage"
      :loading="isDeleting"
      :confirm-text="COPY.DELETE_CONFIRM"
      loading-text="Deleting..."
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, provide, watch } from "vue";
import { message } from "ant-design-vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import AddDomainModal from "@/src/components/external_domain/AddDomainModal.vue";
import ExternalDomainStatusCell from "@/src/components/external_domain/ExternalDomainStatusCell.vue";
import ExternalDomainBoundAppCell from "@/src/components/external_domain/ExternalDomainBoundAppCell.vue";
import ExternalDomainRowActionsCell from "@/src/components/external_domain/ExternalDomainRowActionsCell.vue";
import ExternalDomainService from "@/src/api/ExternalDomainService.js";
import {
  EXTERNAL_DOMAINS_TABLE_HEADERS,
  EXTERNAL_DOMAINS_COPY as COPY,
  TRANSIENT_STATUSES,
  LIST_REFRESH_INTERVAL_MS,
} from "@/src/constants/externalDomainsConstants.js";
import { formatTimestamp } from "@/src/utils/dateTimeUtils.js";

const domains = ref([]);
const initialLoading = ref(true);
const addModalVisible = ref(false);
const viewTarget = ref(null);
const deleteTarget = ref(null);
const deleteModalVisible = ref(false);
const isDeleting = ref(false);
let pollTimer = null;

const customColumnRender = {
  status_label: ExternalDomainStatusCell,
  bound_app_label: ExternalDomainBoundAppCell,
  row_actions: ExternalDomainRowActionsCell,
};

const mappedDomains = computed(() =>
  domains.value.map((item) => ({
    id: item?.id,
    raw: item,
    domain: item?.domain || "—",
    status_label: item?.status,
    bound_app: item?.bound_app || null,
    bound_app_label: item?.bound_app?.name || item?.bound_app?.slug || "",
    added_on_label: item?.created_at
      ? formatTimestamp(item.created_at, "timestamp")
      : "—",
    row_actions: "",
  })),
);

const hasTransientRows = computed(() =>
  mappedDomains.value.some((row) =>
    TRANSIENT_STATUSES.includes(row.status_label),
  ),
);

const deleteModalTitle = computed(() =>
  deleteTarget.value?.bound_app
    ? COPY.DELETE_WHILE_BOUND_TITLE
    : COPY.DELETE_TITLE,
);

const deleteModalMessage = computed(() =>
  deleteTarget.value?.bound_app
    ? COPY.DELETE_WHILE_BOUND_WARNING
    : COPY.DELETE_WARNING,
);

const fetchDomains = async () => {
  try {
    const data = await ExternalDomainService.listWorkspaceDomains();
    domains.value = Array.isArray(data) ? data : [];
  } catch (error) {
    message.error(COPY.GENERIC_ERROR);
    domains.value = [];
  }
};

const refreshList = async () => {
  await fetchDomains();
  syncPollState();
};

const syncPollState = () => {
  if (hasTransientRows.value && !pollTimer) {
    pollTimer = setInterval(() => {
      fetchDomains().then(() => {
        if (!hasTransientRows.value && pollTimer) {
          clearInterval(pollTimer);
          pollTimer = null;
        }
      });
    }, LIST_REFRESH_INTERVAL_MS);
  } else if (!hasTransientRows.value && pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const openAddModal = () => {
  viewTarget.value = null;
  addModalVisible.value = true;
};

const requestDeleteDomain = (row) => {
  deleteTarget.value = row;
  deleteModalVisible.value = true;
};

const requestViewDomain = (row) => {
  if (!row?.raw) return;
  viewTarget.value = row.raw;
  addModalVisible.value = true;
};

watch(addModalVisible, (val) => {
  if (!val) viewTarget.value = null;
});

provide("requestDeleteDomain", requestDeleteDomain);
provide("requestViewDomain", requestViewDomain);

const cancelDelete = () => {
  if (isDeleting.value) return;
  deleteTarget.value = null;
  deleteModalVisible.value = false;
};

const confirmDelete = async () => {
  const target = deleteTarget.value;
  if (!target || isDeleting.value) return;
  isDeleting.value = true;
  try {
    if (target.bound_app?.id) {
      await ExternalDomainService.detachDomainFromApp(
        target.bound_app.id,
        target.id,
      );
    }
    await ExternalDomainService.destroyDomain(target.id);
    message.success("Domain deleted");
    deleteModalVisible.value = false;
    deleteTarget.value = null;
    await refreshList();
  } catch (error) {
    const status = error?.response?.status;
    if (status === 409) {
      message.error(
        "This domain is still attached to an app. Detach it from the app first.",
      );
    } else {
      message.error(COPY.GENERIC_ERROR);
    }
  } finally {
    isDeleting.value = false;
  }
};

onMounted(async () => {
  initialLoading.value = true;
  try {
    await fetchDomains();
  } finally {
    initialLoading.value = false;
    syncPollState();
  }
});

onBeforeUnmount(() => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
});
</script>
